/**
 * Frontend logger abstraction (TASK-079 Phase 2 / FEAT-058).
 *
 * Single funnel through which app code emits diagnostic signals. Replaces
 * direct `console.*` calls (forbidden in production by ESLint `no-console`).
 *
 * In development (`import.meta.env.DEV`):
 *   - Pretty-printed `[level] operation { attrs }` to console.
 *
 * In production:
 *   - debug → no-op
 *   - info  → Sentry breadcrumb (level=info)
 *   - warn  → Sentry breadcrumb (level=warning) + Sentry.captureMessage(op, 'warning')
 *   - error → if attrs.error present: Sentry.captureException(err, { extra: attrs })
 *             else:                   Sentry.captureMessage(op, 'error')
 *
 * Sentry is a no-op transport when not initialized (e.g. dev w/o DSN), so
 * direct calls into the SDK are safe even when `initSentry()` was skipped.
 *
 * PII contract: Callers MUST pass only PII-safe attrs (counts, timings,
 * identifiers, error STRINGS). Per architecture spec, the redaction net is
 * `beforeSend` in lib/sentry.js, but the producer side (this module)
 * shouldn't be the source of leaks either.
 *
 * Spec: memory-bank/creative/TASK-079-phase2-observability.md (Logger Abstraction)
 */

import * as Sentry from '@sentry/react';

const isDev = () => import.meta.env.DEV === true;

function devLog(level, operation, attrs) {
  // Pretty console output for developer ergonomics. ESLint's no-console rule
  // permits 'warn'/'error' globally; the 'log' call is gated behind the dev
  // env check so it's tree-shaken / dead-code in prod builds.
  const payload = attrs && Object.keys(attrs).length > 0 ? attrs : '';
  if (level === 'error') {
    // eslint-disable-next-line no-console
    console.error(`[error] ${operation}`, payload);
  } else if (level === 'warn') {
    // eslint-disable-next-line no-console
    console.warn(`[warn] ${operation}`, payload);
  } else {
    // eslint-disable-next-line no-console
    console.log(`[${level}] ${operation}`, payload);
  }
}

export const logger = {
  /** Detail/debug — dev console only; not emitted in prod. */
  debug(operation, attrs = {}) {
    if (isDev()) {
      devLog('debug', operation, attrs);
    }
    // Prod: intentional no-op.
  },

  /** Routine info — Sentry breadcrumb (level=info) in prod, console in dev. */
  info(operation, attrs = {}) {
    if (isDev()) {
      devLog('info', operation, attrs);
      return;
    }
    Sentry.addBreadcrumb({
      level: 'info',
      message: operation,
      data: attrs,
    });
  },

  /** Recoverable problem — breadcrumb + Sentry.captureMessage(level=warning). */
  warn(operation, attrs = {}) {
    if (isDev()) {
      devLog('warn', operation, attrs);
      return;
    }
    Sentry.addBreadcrumb({
      level: 'warning',
      message: operation,
      data: attrs,
    });
    Sentry.captureMessage(operation, 'warning');
  },

  /**
   * Error — Sentry.captureException(err, { extra }) when attrs.error is given,
   * otherwise Sentry.captureMessage(operation, 'error') so we still surface the
   * event in Sentry without losing context.
   */
  error(operation, attrs = {}) {
    if (isDev()) {
      devLog('error', operation, attrs);
      return;
    }
    if (attrs && attrs.error) {
      Sentry.captureException(attrs.error, { extra: attrs });
    } else {
      Sentry.captureMessage(operation, 'error');
    }
  },
};
